import React from 'react';

import { Chip } from '@mui/material';

import {
  InfiniteApplicationApplicant,
  InfiniteApplicationStatus,
  InfinitePersonTitle,
} from '~/services/LuxLoyaltyInfiniteApplicationService';

export const HEADER_CELL_SX = {
  color: 'text.secondary',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: 'uppercase',
};

const TITLE_LABELS: Record<InfinitePersonTitle, string> = {
  MR: 'Mr',
  MRS: 'Mrs',
  MS: 'Ms',
  MISS: 'Miss',
};

export const STATUS_LABELS: Record<InfiniteApplicationStatus, string> = {
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  FINALIZED: 'Finalised',
};

const STATUS_COLORS: Record<InfiniteApplicationStatus, 'default' | 'info' | 'success' | 'error'> = {
  SUBMITTED: 'default',
  APPROVED: 'info',
  REJECTED: 'error',
  FINALIZED: 'success',
};

const NOTE_DIVIDER = '-----';

export function appendApplicationNote(
  existingNotes: string | null | undefined,
  newNote: string | null | undefined,
  label: string,
): string | undefined {
  const trimmedNote = newNote?.trim();
  if (!trimmedNote) {
    return undefined;
  }

  const trimmedExistingNotes = existingNotes?.trim();
  const timestamp = new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date());
  const noteEntry = `${label} (${timestamp})\n${trimmedNote}`;

  return trimmedExistingNotes ? [trimmedExistingNotes, NOTE_DIVIDER, noteEntry].join('\n') : noteEntry;
}

export function getTitleLabel(title: InfinitePersonTitle | string | null | undefined): string | null {
  if (!title) {
    return null;
  }

  return TITLE_LABELS[title as InfinitePersonTitle] ?? title;
}

export function getDisplayName(person: {
  title?: InfinitePersonTitle | string | null;
  firstName: string;
  lastName: string;
}): string {
  return [getTitleLabel(person.title), person.firstName, person.lastName].filter(Boolean).join(' ');
}

export function getPhone(person: Pick<InfiniteApplicationApplicant, 'phonePrefix' | 'phone'>): string {
  return [person.phonePrefix, person.phone].filter(Boolean).join(' ');
}

export function getAddress(applicant: InfiniteApplicationApplicant): string {
  return [applicant.address, applicant.address2, applicant.city, applicant.state, applicant.postcode, applicant.country]
    .filter(Boolean)
    .join(', ');
}

export function StatusChip({ status }: { status: InfiniteApplicationStatus }) {
  return <Chip size="small" color={STATUS_COLORS[status]} label={STATUS_LABELS[status]} />;
}
